import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, ShieldCheck, Printer, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const ACTION_INFO = {
  antiplagiat: {
    icon: ShieldCheck,
    color: "#0A2E52",
    title: "Test anti-plagiat",
    description:
      "Vous êtes sur le point de déclencher le test anti-plagiat pour ce dépôt. Le Service des Dépôts et l'étudiant en seront automatiquement notifiés.",
  },
  bat: {
    icon: Printer,
    color: "#D4AF37",
    title: "B.A.T — Bon à imprimer",
    description:
      "En confirmant, vous validez officiellement le Bon à imprimer et le document sera transmis au service Reproduction ainsi qu'au Service des Dépôts.",
  },
};

export default function ActionConfirm() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get(`/actions/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [token]);

  const confirm = async () => {
    setConfirming(true);
    setError("");
    try {
      await api.post(`/actions/${token}/confirm`);
      setDone(true);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setConfirming(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Vérification du lien…
      </div>
    );

  if (!info)
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="paper-card p-8 max-w-md text-center">
          <AlertTriangle className="w-10 h-10 text-unisic-red mx-auto" />
          <h1 className="mt-4 text-xl font-bold text-unisic-blue">Lien invalide</h1>
          <p className="mt-2 text-sm text-slate-600">{error || "Ce lien d'action est inconnu ou a expiré."}</p>
          <Link to="/" className="btn-secondary text-sm mt-6 inline-flex">Retour à l'accueil</Link>
        </div>
      </div>
    );

  const spec = ACTION_INFO[info.action] || { title: info.action_label, description: "" };
  const Icon = spec.icon || ShieldCheck;

  if (info.used) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-16">
        <div className="paper-card p-8 max-w-md text-center" data-testid="action-already-used">
          <AlertTriangle className="w-10 h-10 text-unisic-gold mx-auto" />
          <h1 className="mt-4 text-xl font-bold text-unisic-blue">Action déjà confirmée</h1>
          <p className="mt-2 text-sm text-slate-600">
            L'action « {spec.title} » a déjà été effectuée pour ce dépôt le{" "}
            {info.used_at ? new Date(info.used_at).toLocaleString("fr-FR") : ""}.
          </p>
          <Link to="/" className="btn-secondary text-sm mt-6 inline-flex">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-16">
      <div className="paper-card p-10 max-w-xl w-full" data-testid="action-confirm">
        <div className="w-14 h-14 rounded-sm flex items-center justify-center mb-4" style={{ background: `${spec.color}15`, color: spec.color }}>
          <Icon className="w-7 h-7" />
        </div>
        <div className="eyebrow eyebrow-dark">Action Secrétariat</div>
        <h1 className="mt-1 text-2xl font-bold text-unisic-blue">{spec.title}</h1>
        <span className="divider-gold mt-3" />

        <div className="mt-6 space-y-3 text-sm">
          {info.deposit && (
            <div className="rounded-sm bg-slate-50 border border-slate-200 p-4">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Dépôt concerné</div>
              <div className="mt-2 font-mono text-sm text-unisic-blue">{info.deposit.tracking_number}</div>
              <div className="mt-1 text-sm text-slate-800">{info.deposit.subject}</div>
              <div className="mt-1 text-xs text-slate-600">
                {info.deposit.kind === "memoire" ? "Mémoire" : "Projet tutoré"} · Étudiant/Groupe : {info.deposit.student_name || "—"}
              </div>
            </div>
          )}
          <p className="text-slate-600">{spec.description}</p>
        </div>

        {done ? (
          <div className="mt-6 flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-sm text-green-800 text-sm" data-testid="action-done">
            <CheckCircle2 className="w-5 h-5" /> Action confirmée avec succès. Les emails ont été envoyés.
          </div>
        ) : (
          <div className="mt-8 flex gap-3">
            <button
              onClick={confirm}
              disabled={confirming}
              className="btn-primary text-sm justify-center flex-1"
              data-testid="btn-confirm-action"
            >
              {confirming && <Loader2 className="w-4 h-4 animate-spin" />}
              Confirmer l'action
            </button>
            <Link to="/" className="btn-secondary text-sm">Annuler</Link>
          </div>
        )}

        {error && !done && (
          <div className="mt-4 text-xs text-unisic-red" data-testid="action-error">{error}</div>
        )}
      </div>
    </div>
  );
}
